import React from 'react';
import { Plus, Check, Star, Copy, Lightbulb } from 'lucide-react';
import { useAskSidebar, ContextItem } from '../../contexts/AskSidebarContext';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface EntryActionsProps {
  /** Data to build the ContextItem when added to the basket */
  item: ContextItem;
  /** Compact mode: smaller icons, tighter spacing (for inline/row items) */
  compact?: boolean;
  /** Extra classes on the wrapper */
  className?: string;
  /** If provided, renders a copy button that calls this to get text for clipboard.
   *  Can return a plain string or { text, html } for rich paste into Word. */
  onCopy?: () => string | { text: string; html: string };
  /** If provided, renders an explain button */
  onExplain?: () => void;
  /** Whether the explain panel is currently active */
  isExplaining?: boolean;
  /** Whether the explain call is loading */
  isExplainLoading?: boolean;
}

/**
 * Action icons for any entry/card:
 * 1. Pin to chat — adds the item to the context basket for the AI assistant
 * 2. Key Finding — marks as a key finding
 * 3. Copy — copies to clipboard (optional)
 */
export const EntryActions: React.FC<EntryActionsProps> = ({
  item,
  compact = false,
  className = '',
  onCopy,
  onExplain,
  isExplaining = false,
  isExplainLoading = false,
}) => {
  const { addToContext, removeFromContext, isInContext, open } = useAskSidebar();
  const { isEventMarked, addFinding, getFindingsForEvent, removeFinding } = useKeyFindings();
  const { copied, copy } = useCopyToClipboard();

  const inContext = isInContext(item.id);
  const isMarked = isEventMarked(item.id);

  const handleToggleContext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inContext) {
      removeFromContext(item.id);
    } else {
      addToContext(item);
      open();
    }
  };

  const handleToggleFinding = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMarked) {
      const findings = getFindingsForEvent(item.id);
      // Remove the whole-event finding (no selectedText, or for non-event types the initial mark)
      const wholeEventFinding = findings.find(f => !f.startOffset);
      if (wholeEventFinding) {
        removeFinding(wholeEventFinding.id);
      }
    } else {
      // For non-chronology items (expert opinions etc.), the backend can't look up
      // a headline from the eventId, so pass the summary as selectedText
      const isChronologyEvent = item.type === 'event';
      addFinding({
        eventId: item.id,
        ...(!isChronologyEvent && item.summary ? { selectedText: item.summary } : {}),
      });
    }
  };

  const sz = compact ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const pad = compact ? 'p-1.5' : 'p-1.5';

  return (
    <div
      className={`flex items-center gap-1 flex-shrink-0 ${className}`}
      // Prevent clicks on the icons from toggling the parent card
      onClick={e => e.stopPropagation()}
    >
      {/* Pin to chat */}
      <button
        onClick={handleToggleContext}
        className={`relative group/action ${pad} rounded transition-colors ${
          inContext
            ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400'
            : 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-400 dark:text-cyan-500 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 hover:text-cyan-600 dark:hover:text-cyan-400'
        }`}
      >
        {inContext ? <Check className={sz} /> : <Plus className={sz} />}
        <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 pointer-events-none transition-opacity z-50">
          {inContext ? 'Unpin from chat' : 'Pin to chat'}
        </span>
      </button>

      {/* Mark/unmark as key finding */}
      <button
        onClick={handleToggleFinding}
        className={`relative group/action ${pad} rounded transition-colors ${
          isMarked
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400'
            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-400 dark:text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:text-amber-500 dark:hover:text-amber-400'
        }`}
      >
        <Star className={sz} fill={isMarked ? 'currentColor' : 'none'} />
        <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 pointer-events-none transition-opacity z-50">
          {isMarked ? 'Unmark' : 'Key Finding'}
        </span>
      </button>

      {/* Explain event provenance */}
      {onExplain && (
        <button
          onClick={(e) => { e.stopPropagation(); if (!isExplainLoading) onExplain(); }}
          disabled={isExplainLoading}
          className={`relative group/action ${pad} rounded transition-colors ${
            isExplainLoading
              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400 cursor-default'
              : isExplaining
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 dark:text-emerald-400'
                : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-400 dark:text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:text-emerald-600 dark:hover:text-emerald-400'
          }`}
        >
          <Lightbulb
            className={sz}
            fill={isExplainLoading ? 'currentColor' : 'none'}
            style={isExplainLoading ? { animation: 'lightbulb-warm 1.5s ease-in-out infinite' } : undefined}
          />
          <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 pointer-events-none transition-opacity z-50">
            {isExplainLoading ? 'Analysing…' : isExplaining ? 'Hide explanation' : 'Explain'}
          </span>
        </button>
      )}

      {/* Copy to clipboard */}
      {onCopy && (
        <button
          onClick={(e) => { e.stopPropagation(); const r = onCopy(); typeof r === 'string' ? copy(r) : copy(r.text, r.html); }}
          className={`relative group/action ${pad} rounded transition-colors ${
            copied
              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
              : 'bg-gray-50 dark:bg-gray-800/30 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700/30 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        >
          {copied ? <Check className={sz} /> : <Copy className={sz} />}
          <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 pointer-events-none transition-opacity z-50">
            {copied ? 'Copied!' : 'Copy'}
          </span>
        </button>
      )}
    </div>
  );
};
